import { Component, Element, Event, EventEmitter, Method, Prop, State, Watch, h } from '@stencil/core';
import Bind from 'bind-decorator';
import { capitalize } from '../../../utils/capitalize';
import { generateCssClasses } from '../../../utils/generate-css-classes';
import { generateUniqueUuid } from '../../../utils/generate-unique-uuid';
import { hasSlotted } from '../../../utils/slotted';
import { LocaleService } from '../../zds-config/locale-service';
import { handlePanelAnimationsOpen, handlePanelAnimationsClosed } from '../animations-util';
import { NavigationEventDetail } from '../zds-navigation';

type NavigationPanelState = 'open' | 'opened' | 'close' | 'closed';

@Component({
	shadow: true,
	styleUrl: 'zds-navigation-panel.scss',
	tag: 'zds-navigation-panel',
})
export class ZdsNavigationPanel {
	timeout: ReturnType<typeof setTimeout>;
	panelElement: HTMLDivElement;

	@Element() hostElement: HTMLDivElement;

	@Event({ bubbles: true }) zdsNavigationActivateBody: EventEmitter<NavigationEventDetail>;
	@Event({ bubbles: true }) zdsNavigationActivatePanel: EventEmitter<NavigationEventDetail>;

	@Event({ bubbles: true }) zdsNavigationPanelClose: EventEmitter<NavigationEventDetail>;
	@Event({ bubbles: true }) zdsNavigationPanelClosed: EventEmitter<NavigationEventDetail>;
	@Event({ bubbles: true }) zdsNavigationPanelOpen: EventEmitter<NavigationEventDetail>;
	@Event({ bubbles: true }) zdsNavigationPanelOpened: EventEmitter<NavigationEventDetail>;

	@Prop() header: string;
	@Prop() identifier: string = generateUniqueUuid('zds-navigation-panel');

	@State() state: NavigationPanelState = 'closed';

	@Watch('state')
	onStateChange(newValue: string) {
		this[`zdsNavigationPanel${capitalize(newValue)}`].emit({ identifier: this.identifier });
	}

	@Method()
	async toggle(toggle: boolean = this.state === 'close' || this.state === 'closed') {
		clearTimeout(this.timeout);

		this.state = toggle ? 'open' : 'close';
		this.timeout = setTimeout(this.finishStateChange, 400, this.state);

		// Make sure the element is visible, otherwise animations will not be executed
		if (toggle) {
			this.panelElement.style.display = 'block';
		}

		if (this.state === 'open') {
			requestAnimationFrame(() => {
				handlePanelAnimationsOpen(this.hostElement);
			});
		}
	}

	componentDidLoad() {
		handlePanelAnimationsClosed(this.hostElement);
	}

	@Bind
	finishStateChange(state: NavigationPanelState) {
		if (state === 'open') {
			this.state = 'opened';
		} else if (state === 'close') {
			this.state = 'closed';
			this.panelElement.style.display = 'none';
			handlePanelAnimationsClosed(this.hostElement);
		}
	}

	@Bind
	async onBackClick(event: MouseEvent) {
		event.preventDefault();
		await this.toggle(false);

		// Update zds-navigation and empty the activePanel property by not passing any event details
		this.zdsNavigationActivatePanel.emit({ identifier: null });
	}

	@Bind
	onCloseClick(event: MouseEvent) {
		event.preventDefault();

		// Update zds-navigation and empty the activePanel property by not passing an identifier
		this.zdsNavigationActivateBody.emit({ identifier: null });
	}

	render() {
		const panelClasses = generateCssClasses('zds-navigation-panel__panel', {
			[this.state]: true,
		});

		return (
			<div class="zds-navigation-panel">
				<div class="zds-navigation-panel__body-link">
					<slot name="body-link" />
				</div>
				<div class={panelClasses} ref={(element) => (this.panelElement = element)}>
					<div class="zds-navigation-panel__body">
						<span class="zds-navigation-panel__header">
							<a
								href="#"
								class="zds-navigation-panel__header__button"
								onClick={(event) => void this.onBackClick(event)}
								aria-label={LocaleService.t('zdsNavigationPanel.backLabel')}
							>
								<zds-icon
									class="zds-navigation-panel__header__button-icon"
									name="chevron-left"
									library="fa-solid"
								></zds-icon>
							</a>
							{this.header}
							<a
								aria-label={LocaleService.t('zdsNavigationPanel.closeLabel')}
								href="#"
								class="zds-navigation-panel__header__button"
								onClick={this.onCloseClick}
							>
								<zds-icon
									class="zds-navigation-panel__header__button-icon"
									name="xmark"
									library="fa-solid"
								></zds-icon>
							</a>
						</span>
						{this.renderTitle()}
						{this.renderSubText()}
						<div class="zds-navigation-panel__panel-content">
							<slot />
						</div>
					</div>
				</div>
			</div>
		);
	}

	renderSubText() {
		if (!hasSlotted(this.hostElement, 'subtext')) {
			return null;
		}

		return (
			<div class="zds-navigation-panel__subtext">
				<slot name="subtext"></slot>
			</div>
		);
	}

	renderTitle() {
		if (!hasSlotted(this.hostElement, 'title')) {
			return null;
		}

		return (
			<div class="zds-navigation-panel__title">
				<slot name="title"></slot>
			</div>
		);
	}
}
